/* order-status-onne.js — ONNE "Estado de mi pedido" (canary).
   Custom element <order-status-onne>. Habla con el backend del sales-agent:
   POST {endpoint}/api/support/order/lookup  {order_number, second_factor, lang}.
   Regla dura: nunca afirma algo que no ocurrió; tracking solo si llega tracking_url real.
   Horizon-ONNE conventions (handbook 09): custom element, connectedCallback, sin IIFE. */

const COPY = {
  es: {
    title: 'Estado de mi pedido',
    subtitle: 'Mira dónde está tu pedido en un momento',
    order_label: 'Número de pedido',
    order_ph: 'Empieza por ONNE o por #',
    factor_label: 'Email o últimos 4 del teléfono',
    factor_help: 'El que usaste en la compra',
    factor_ph: 'tu@email.com',
    cta: 'Ver mi pedido',
    no_order: 'No encuentro mi número de pedido',
    loading: 'Buscando tu pedido…',
    step_prep: 'En preparación',
    step_ship: 'Enviado',
    step_deliv: 'Entregado',
    talk_team: 'Hablar con el equipo',
    retry: 'Intentar de nuevo',
    need_order: 'Indícanos tu número de pedido 🤍',
    need_factor: 'Añade el email o los últimos 4 del teléfono 🤍',
    verify_retry: 'Mmm, esos datos no me cuadran con ningún pedido. ¿Los revisamos? Necesito el número de pedido y el email o los últimos 4 dígitos del teléfono de la compra.',
    verify_failed: 'Sigo sin poder confirmarlo y no quiero darte datos de un pedido que no sea tuyo. 🤍 Te paso con nuestro equipo para que lo revise contigo.',
    order_word: 'Tu pedido',
    status_preparing: 'está en preparación 🤍 — te avisamos en cuanto salga de nuestro almacén',
    status_in_transit: 'va de camino con {carrier} 🌊',
    status_delivered: 'ya está entregado ☀️ — esperamos que lo disfrutes; si algo no encaja, cuéntanos',
    status_incident: 'Hemos mirado tu pedido {order} y ha tenido una incidencia con el envío. Sentimos las molestias de verdad. 🤍 Prefiero no darte una fecha que no pueda cumplir: lo paso a nuestro equipo y te escribimos en cuanto sepamos más.',
    status_checking: 'Estoy comprobando el estado de tu pedido {order} 🤍 — dame un momento; si no consigo confirmarlo al instante, lo paso a nuestro equipo y te escribimos enseguida.',
    track: 'Seguir mi envío',
    trk_carrier: 'Transportista',
    trk_number: 'Nº de seguimiento',
    trk_updated: 'Última actualización',
    trk_live_hint: 'Seguimiento completo en directo',
    carrier_fallback: 'tu transportista',
    service_unavailable: 'Estamos haciendo unos ajustes y esta consulta no está disponible ahora mismo. Vuelve en un ratito o escríbenos 🤍',
    network_error: 'Ups, no hemos podido conectar. ¿Probamos otra vez?',
    no_order_graceful: '¿No encuentras tu número de pedido? Sin problema 🤍 Suele estar en el email de confirmación (busca «ÔNNE»). Si no lo localizas, te paso con nuestro equipo y lo encontramos contigo.',
    find_title: 'Te buscamos tu pedido',
    find_intro: 'Dinos el email y el teléfono (últimos 4) o el código postal de la compra y te damos tu número de pedido al momento 🤍',
    find_email_label: 'Email de la compra',
    find_email_ph: 'tu@email.com',
    find_factor_label: 'Teléfono (últimos 4) o código postal',
    find_factor_ph: 'Ej. 1234 o 03600',
    find_cta: 'Buscar mi pedido',
    find_found: '¡Lo tenemos! Tu pedido es {order} 🤍',
    find_not_found: 'No hemos podido verificarlo con esos datos y no quiero darte un pedido que no sea tuyo. 🤍 Te paso con nuestro equipo para encontrarlo contigo.',
    need_email: 'Necesitamos un email válido 🤍',
    need_find_factor: 'Añade el teléfono (últimos 4) o el código postal de la compra 🤍',
  },
  en: {
    title: 'My order status',
    subtitle: 'See where your order is in a moment',
    order_label: 'Order number',
    order_ph: 'Starts with ONNE or #',
    factor_label: 'Email or last 4 of phone',
    factor_help: 'The one used at checkout',
    factor_ph: 'you@email.com',
    cta: 'See my order',
    no_order: "I can't find my order number",
    loading: 'Looking up your order…',
    step_prep: 'Preparing',
    step_ship: 'Shipped',
    step_deliv: 'Delivered',
    talk_team: 'Talk to our team',
    retry: 'Try again',
    need_order: 'Please share your order number 🤍',
    need_factor: 'Add the email or last 4 digits of the phone 🤍',
    verify_retry: "Hmm, those details don't match any order. Shall we check again? I need the order number and the email or last 4 digits of the phone used at checkout.",
    verify_failed: "I still can't confirm it, and I don't want to share details of an order that isn't yours. 🤍 Let me connect you with our team to check it with you.",
    order_word: 'Your order',
    status_preparing: 'is being prepared 🤍 — we\'ll let you know as soon as it leaves our warehouse',
    status_in_transit: 'is on its way with {carrier} 🌊',
    status_delivered: 'has been delivered ☀️ — we hope you love it; if something\'s not right, tell us',
    status_incident: "We've checked your order {order} and there's been a shipping issue. We're truly sorry. 🤍 I'd rather not give you a date I can't keep: I'm passing it to our team and we'll write as soon as we know more.",
    status_checking: "I'm checking the status of your order {order} 🤍 — give me a moment; if I can't confirm it right away, I'll pass it to our team and we'll write back shortly.",
    track: 'Track my shipment',
    trk_carrier: 'Carrier',
    trk_number: 'Tracking number',
    trk_updated: 'Last update',
    trk_live_hint: 'Full live tracking',
    carrier_fallback: 'your carrier',
    service_unavailable: "We're making a few tweaks and this isn't available right now. Check back in a bit or write to us 🤍",
    network_error: "Oops, we couldn't connect. Shall we try again?",
    no_order_graceful: "Can't find your order number? No problem 🤍 It's usually in your confirmation email (search «ÔNNE»). If you can't find it, I'll connect you with our team and we'll find it together.",
    find_title: "Let's find your order",
    find_intro: "Tell us the email and the phone (last 4) or postal code from your purchase and we'll give you your order number right away 🤍",
    find_email_label: 'Email used at checkout',
    find_email_ph: 'you@email.com',
    find_factor_label: 'Phone (last 4) or postal code',
    find_factor_ph: 'e.g. 1234 or 03600',
    find_cta: 'Find my order',
    find_found: 'Got it! Your order is {order} 🤍',
    find_not_found: "We couldn't verify it with those details and I don't want to give you an order that isn't yours. 🤍 I'll connect you with our team to find it together.",
    need_email: 'We need a valid email 🤍',
    need_find_factor: 'Add the phone (last 4) or the postal code from your purchase 🤍',
  },
  it: {
    title: 'Stato del mio ordine',
    subtitle: 'Guarda dov\'è il tuo ordine in un attimo',
    order_label: 'Numero d\'ordine',
    order_ph: 'Inizia con ONNE o con #',
    factor_label: 'Email o ultime 4 cifre del telefono',
    factor_help: 'Quello usato all\'acquisto',
    factor_ph: 'tu@email.com',
    cta: 'Vedi il mio ordine',
    no_order: 'Non trovo il mio numero d\'ordine',
    loading: 'Cerco il tuo ordine…',
    step_prep: 'In preparazione',
    step_ship: 'Spedito',
    step_deliv: 'Consegnato',
    talk_team: 'Parla con il team',
    retry: 'Riprova',
    need_order: 'Indicaci il tuo numero d\'ordine 🤍',
    need_factor: 'Aggiungi l\'email o le ultime 4 cifre del telefono 🤍',
    verify_retry: 'Mmm, questi dati non corrispondono a nessun ordine. Riproviamo? Mi serve il numero d\'ordine e l\'email o le ultime 4 cifre del telefono dell\'acquisto.',
    verify_failed: 'Non riesco ancora a confermarlo e non voglio darti i dati di un ordine che non è tuo. 🤍 Ti metto in contatto con il team per verificarlo insieme a te.',
    order_word: 'Il tuo ordine',
    status_preparing: 'è in preparazione 🤍 — ti avvisiamo appena parte dal magazzino',
    status_in_transit: 'è in viaggio con {carrier} 🌊',
    status_delivered: 'è stato consegnato ☀️ — speriamo ti piaccia; se qualcosa non va, diccelo',
    status_incident: 'Abbiamo controllato il tuo ordine {order} e c\'è stato un problema con la spedizione. Ci dispiace davvero. 🤍 Preferisco non darti una data che non posso garantire: lo passo al team e ti scriviamo appena sappiamo di più.',
    status_checking: 'Sto controllando lo stato del tuo ordine {order} 🤍 — dammi un momento; se non riesco a confermarlo subito, lo passo al team e ti scriviamo a breve.',
    track: 'Segui la spedizione',
    trk_carrier: 'Corriere',
    trk_number: 'Numero di tracking',
    trk_updated: 'Ultimo aggiornamento',
    trk_live_hint: 'Tracciamento completo in diretta',
    carrier_fallback: 'il tuo corriere',
    service_unavailable: 'Stiamo facendo alcune modifiche e ora non è disponibile. Torna tra poco o scrivici 🤍',
    network_error: 'Ops, non siamo riusciti a connetterci. Riproviamo?',
    no_order_graceful: 'Non trovi il numero d\'ordine? Nessun problema 🤍 Di solito è nell\'email di conferma (cerca «ÔNNE»). Se non lo trovi, ti metto in contatto con il team e lo troviamo insieme.',
    find_title: 'Troviamo il tuo ordine',
    find_intro: 'Dicci l\'email e il telefono (ultime 4) o il codice postale dell\'acquisto e ti diamo subito il numero d\'ordine 🤍',
    find_email_label: 'Email dell\'acquisto',
    find_email_ph: 'tu@email.com',
    find_factor_label: 'Telefono (ultime 4) o codice postale',
    find_factor_ph: 'Es. 1234 o 03600',
    find_cta: 'Trova il mio ordine',
    find_found: 'Trovato! Il tuo ordine è {order} 🤍',
    find_not_found: 'Non siamo riusciti a verificarlo con questi dati e non voglio darti un ordine che non è tuo. 🤍 Ti metto in contatto con il team per trovarlo insieme.',
    need_email: 'Ci serve un\'email valida 🤍',
    need_find_factor: 'Aggiungi il telefono (ultime 4) o il codice postale dell\'acquisto 🤍',
  },
  pt: {
    title: 'Estado da minha encomenda',
    subtitle: 'Vê onde está a tua encomenda num instante',
    order_label: 'Número da encomenda',
    order_ph: 'Começa por ONNE ou por #',
    factor_label: 'Email ou últimos 4 do telemóvel',
    factor_help: 'O que usaste na compra',
    factor_ph: 'tu@email.com',
    cta: 'Ver a minha encomenda',
    no_order: 'Não encontro o meu número de encomenda',
    loading: 'A procurar a tua encomenda…',
    step_prep: 'Em preparação',
    step_ship: 'Enviado',
    step_deliv: 'Entregue',
    talk_team: 'Falar com a equipa',
    retry: 'Tentar de novo',
    need_order: 'Indica o teu número de encomenda 🤍',
    need_factor: 'Adiciona o email ou os últimos 4 dígitos do telemóvel 🤍',
    verify_retry: 'Hmm, esses dados não correspondem a nenhuma encomenda. Vamos verificar? Preciso do número da encomenda e do email ou dos últimos 4 dígitos do telemóvel da compra.',
    verify_failed: 'Continuo sem conseguir confirmar e não quero dar-te dados de uma encomenda que não seja tua. 🤍 Vou pôr-te em contacto com a equipa para verificar contigo.',
    order_word: 'A tua encomenda',
    status_preparing: 'está em preparação 🤍 — avisamos-te assim que sair do armazém',
    status_in_transit: 'vai a caminho com {carrier} 🌊',
    status_delivered: 'já foi entregue ☀️ — esperamos que adores; se algo não estiver bem, diz-nos',
    status_incident: 'Vimos a tua encomenda {order} e houve uma incidência com o envio. Lamentamos mesmo. 🤍 Prefiro não te dar uma data que não possa cumprir: passo à equipa e escrevemos assim que soubermos mais.',
    status_checking: 'Estou a verificar o estado da tua encomenda {order} 🤍 — dá-me um momento; se não conseguir confirmar já, passo à equipa e escrevemos em breve.',
    track: 'Seguir o meu envio',
    trk_carrier: 'Transportadora',
    trk_number: 'Nº de seguimento',
    trk_updated: 'Última atualização',
    trk_live_hint: 'Seguimento completo em direto',
    carrier_fallback: 'a tua transportadora',
    service_unavailable: 'Estamos a fazer uns ajustes e isto não está disponível agora. Volta daqui a pouco ou escreve-nos 🤍',
    network_error: 'Ups, não conseguimos ligar. Tentamos de novo?',
    no_order_graceful: 'Não encontras o número da encomenda? Sem problema 🤍 Costuma estar no email de confirmação (procura «ÔNNE»). Se não o encontrares, ponho-te em contacto com a equipa e encontramo-lo contigo.',
    find_title: 'Encontramos a tua encomenda',
    find_intro: 'Diz-nos o email e o telefone (últimos 4) ou o código postal da compra e damos-te já o teu número de encomenda 🤍',
    find_email_label: 'Email da compra',
    find_email_ph: 'tu@email.com',
    find_factor_label: 'Telefone (últimos 4) ou código postal',
    find_factor_ph: 'Ex. 1234 ou 03600',
    find_cta: 'Encontrar a minha encomenda',
    find_found: 'Encontrámos! A tua encomenda é {order} 🤍',
    find_not_found: 'Não conseguimos verificar com esses dados e não quero dar-te uma encomenda que não seja tua. 🤍 Ponho-te em contacto com a equipa para a encontrar contigo.',
    need_email: 'Precisamos de um email válido 🤍',
    need_find_factor: 'Adiciona o telefone (últimos 4) ou o código postal da compra 🤍',
  },
};

const ICON_CHECK = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.5 8.5l3 3 6-6.5"/></svg>';
const ICON_ARROW = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 8h9M8.5 4l4 4-4 4"/></svg>';

class OrderStatusOnne extends HTMLElement {
  connectedCallback() {
    // Prefer the visitor's BROWSER language — the storefront is EN-only so data-lang
    // is always 'en'. Falls back to data-lang, then 'es'.
    const _supp = ['es', 'en', 'it', 'pt'];
    const _pick = (v) => { v = (v || '').slice(0, 2).toLowerCase(); return _supp.indexOf(v) >= 0 ? v : ''; };
    const lang = _pick(navigator.language) || _pick(this.dataset.lang) || 'es';
    this.t = COPY[lang] || COPY.es;
    this.lang = lang;
    this.endpoint = (this.dataset.endpoint || '').replace(/\/+$/, '');
    this.contact = this.dataset.contact || 'mailto:info@onne.com';
    this.demo = this.dataset.demo === 'on';
    this.root = this.querySelector('[data-support-root]') || this;

    const forced = new URLSearchParams(location.search).get('demo');
    if (this.demo && forced) {
      this._renderState(forced, 'ONNE10482');
      return;
    }
    this.renderIdentify();
  }

  // -------------------------------------------------------------- helpers
  _set(html) { this.root.innerHTML = html; }

  _talkTeamLink() {
    const url = this.contact;
    return `<a class="support-onne__link support-onne__link--center" href="${url}">${this.t.talk_team}</a>`;
  }

  _detectFactor(raw) {
    const v = (raw || '').trim();
    if (v.includes('@')) return { email: v };
    const digits = v.replace(/\D/g, '');
    if (digits.length >= 4) return { phone_last4: digits.slice(-4) };
    return null;
  }

  // -------------------------------------------------------------- identify form
  renderIdentify(notice) {
    const t = this.t;
    this._set(`
      <h1 class="support-onne__title">${t.title}</h1>
      <p class="support-onne__subtitle">${t.subtitle}</p>
      ${notice ? `<div class="support-onne__state" style="margin-bottom:1.25rem"><p class="support-onne__state-msg">${notice}</p></div>` : ''}
      <form class="support-onne__card support-onne__card--narrow" data-form novalidate>
        <div class="support-onne__field" data-field-order>
          <label class="support-onne__label" for="os-order">${t.order_label}</label>
          <input class="support-onne__input" id="os-order" name="order" type="text" autocomplete="off"
                 inputmode="text" placeholder="${t.order_ph}" />
          <p class="support-onne__error" data-err-order hidden></p>
        </div>
        <div class="support-onne__field" data-field-factor>
          <label class="support-onne__label" for="os-factor">${t.factor_label}</label>
          <input class="support-onne__input" id="os-factor" name="factor" type="text" autocomplete="off"
                 placeholder="${t.factor_ph}" />
          <p class="support-onne__help">${t.factor_help}</p>
          <p class="support-onne__error" data-err-factor hidden></p>
        </div>
        <button class="support-onne__btn" type="submit" data-submit>${t.cta}</button>
        <button class="support-onne__link support-onne__link--center" type="button" data-no-order>${t.no_order}</button>
      </form>
    `);

    const form = this.root.querySelector('[data-form]');
    form.addEventListener('submit', (e) => { e.preventDefault(); this._onSubmit(form); });
    this.root.querySelector('[data-no-order]').addEventListener('click', () => this.renderNoOrder());
  }

  _fieldError(form, which, msg) {
    const field = form.querySelector(`[data-field-${which}]`);
    const err = form.querySelector(`[data-err-${which}]`);
    field.classList.add('support-onne__field--error');
    err.textContent = msg;
    err.hidden = false;
  }

  _onSubmit(form) {
    const t = this.t;
    const orderEl = form.querySelector('#os-order');
    const factorEl = form.querySelector('#os-factor');
    form.querySelectorAll('.support-onne__field').forEach((f) => f.classList.remove('support-onne__field--error'));
    form.querySelectorAll('.support-onne__error').forEach((e) => { e.hidden = true; });

    const orderNumber = (orderEl.value || '').trim();
    const factorRaw = (factorEl.value || '').trim();
    // "Pedir solo lo que falta": resaltar únicamente el campo vacío.
    let bad = false;
    if (!orderNumber) { this._fieldError(form, 'order', t.need_order); bad = true; }
    const factor = this._detectFactor(factorRaw);
    if (!factor) { this._fieldError(form, 'factor', t.need_factor); bad = true; }
    if (bad) { (orderNumber ? factorEl : orderEl).focus(); return; }

    this._lookup(orderNumber, factor);
  }

  // -------------------------------------------------------------- network
  renderLoading() {
    this._set(`<div class="support-onne__loading" role="status" aria-live="polite">${this.t.loading}</div>`);
  }

  async _lookup(orderNumber, factor) {
    this.renderLoading();
    if (this.demo) { this._renderState('preparing', orderNumber); return; }
    if (!this.endpoint) { this.renderUnavailable(); return; }

    let resp;
    try {
      resp = await fetch(`${this.endpoint}/api/support/order/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_number: orderNumber, second_factor: factor, lang: this.lang }),
      });
    } catch (e) {
      this.renderNetworkError(orderNumber, factor);
      return;
    }
    if (resp.status === 503) { this.renderUnavailable(); return; }
    let data;
    try { data = await resp.json(); } catch (e) { this.renderNetworkError(orderNumber, factor); return; }

    if (data && data.verified === true) { this.renderResult(data, orderNumber); return; }
    if (data && data.escalate) { this.renderFailed(); return; }
    this.renderIdentify(this.t.verify_retry);
  }

  // -------------------------------------------------------------- result states
  renderResult(data, orderNumber) {
    // Real carrier incident (cancelled / stuck parcel) → honest shipping-issue message.
    if (data.incident) { this.renderIncident(orderNumber); return; }
    // 'unknown' is NOT an incident — it just means we couldn't bucket the state
    // (e.g. a refunded order). Show a NEUTRAL "we're checking" message, never the
    // alarming shipping-issue copy. (Diego 2026-06-16.)
    if (data.status === 'unknown') { this.renderChecking(orderNumber); return; }
    const order = { processing: 0, shipped: 1, delivered: 2 };
    const idx = order[data.status] != null ? order[data.status] : 0;
    const t = this.t;

    const steps = [t.step_prep, t.step_ship, t.step_deliv].map((label, i) => {
      const cls = i < idx ? 'order-status-onne__step--done' : (i === idx ? 'order-status-onne__step--current' : '');
      const dot = i < idx ? ICON_CHECK : '';
      const line = i < 2 ? `<div class="order-status-onne__line${i < idx ? ' order-status-onne__line--done' : ''}"></div>` : '';
      return `<div class="order-status-onne__step ${cls}"><div class="order-status-onne__dot">${dot}</div><div class="order-status-onne__step-label">${label}</div></div>${line}`;
    }).join('');

    let statusText = [t.status_preparing, t.status_in_transit, t.status_delivered][idx];
    if (idx === 1) {
      const carrier = (data.carrier_status && (data.carrier_status.carrier || data.carrier_status.name)) || t.carrier_fallback;
      statusText = statusText.replace('{carrier}', carrier);
    }

    const cs = data.carrier_status || {};
    const url = data.tracking_url ? encodeURI(data.tracking_url) : '';

    // Premium detail rows — only what we actually have from the carrier.
    const rows = [];
    if (cs.carrier) rows.push(`<div class="order-status-onne__row"><span class="order-status-onne__row-k">${t.trk_carrier}</span><span class="order-status-onne__row-v">${this._carrierName(cs.carrier)}</span></div>`);
    if (cs.tracking_number) rows.push(`<div class="order-status-onne__row"><span class="order-status-onne__row-k">${t.trk_number}</span><button class="order-status-onne__copy" type="button" data-copy><span class="js-trk"></span><span class="order-status-onne__copy-ic" aria-hidden="true">⧉</span></button></div>`);
    if (cs.last_event_date) rows.push(`<div class="order-status-onne__row"><span class="order-status-onne__row-k">${t.trk_updated}</span><span class="order-status-onne__row-v js-upd"></span></div>`);

    const track = url ? `<a class="support-onne__btn order-status-onne__track" href="${url}" target="_blank" rel="noopener">${t.track}</a>` : '';
    // Premium: embed the SendCloud branded portal (full live carrier timeline) below.
    const embed = url ? `<div class="order-status-onne__live">
        <p class="order-status-onne__live-h">${t.trk_live_hint}</p>
        <iframe class="order-status-onne__iframe" src="${url}" title="${t.track}" loading="lazy"></iframe>
      </div>` : '';

    this._set(`
      <p class="order-status-onne__order-ref"><span class="js-order"></span></p>
      <div class="order-status-onne__stepper" role="img" aria-label="${[t.step_prep, t.step_ship, t.step_deliv][idx]}">${steps}</div>
      <div class="order-status-onne__status support-onne__card--narrow">
        <p style="margin:0 0 ${rows.length ? '0.9rem' : '0'}"><strong class="js-order2"></strong> <span class="js-status"></span></p>
        ${rows.length ? `<div class="order-status-onne__rows">${rows.join('')}</div>` : ''}
        ${track}
      </div>
      ${embed}
      <a class="support-onne__link support-onne__link--center" href="${this.contact}">${t.talk_team}</a>
    `);
    this.root.querySelector('.js-order').textContent = `${t.order_word} ${orderNumber}`;
    this.root.querySelector('.js-order2').textContent = `${t.order_word} ${orderNumber}`;
    this.root.querySelector('.js-status').textContent = statusText;
    const updEl = this.root.querySelector('.js-upd');
    if (updEl) updEl.textContent = this._fmtDate(cs.last_event_date);
    const trkEl = this.root.querySelector('.js-trk');
    if (trkEl) trkEl.textContent = cs.tracking_number;
    const copyBtn = this.root.querySelector('[data-copy]');
    if (copyBtn) copyBtn.addEventListener('click', () => {
      if (navigator.clipboard) navigator.clipboard.writeText(cs.tracking_number).catch(() => {});
      const ic = copyBtn.querySelector('.order-status-onne__copy-ic');
      if (ic) { ic.textContent = '✓'; setTimeout(() => { ic.textContent = '⧉'; }, 1400); }
    });
  }

  _carrierName(code) {
    const m = { correos: 'Correos', seur: 'SEUR', gls: 'GLS', ups: 'UPS', dhl: 'DHL', dpd: 'DPD', sendcloud: 'Correos' };
    const c = String(code || '').toLowerCase();
    return m[c] || (code ? code.charAt(0).toUpperCase() + code.slice(1) : '');
  }

  _fmtDate(s) {
    if (!s) return '';
    const str = String(s).trim();
    const m = str.match(/^(\d{2})[-/](\d{2})[-/](\d{4})(?:[ T](\d{2}):(\d{2}))?/);
    return m ? `${m[1]}/${m[2]}/${m[3]}${m[4] ? `, ${m[4]}:${m[5]}` : ''}` : str;
  }

  renderIncident(orderNumber) {
    const msg = this.t.status_incident.replace('{order}', orderNumber);
    this._set(`
      <div class="support-onne__state support-onne__card support-onne__card--narrow">
        <p class="support-onne__state-msg js-msg"></p>
        ${this._talkTeamLink()}
      </div>
    `);
    this.root.querySelector('.js-msg').textContent = msg;
  }

  // Neutral state for an order we couldn't bucket (NOT an incident): we're
  // checking and will write back. Reassuring, never alarming.
  renderChecking(orderNumber) {
    const msg = (this.t.status_checking || '').replace('{order}', orderNumber);
    this._set(`
      <div class="support-onne__state support-onne__card support-onne__card--narrow">
        <p class="support-onne__state-msg js-msg"></p>
        ${this._talkTeamLink()}
      </div>
    `);
    this.root.querySelector('.js-msg').textContent = msg;
  }

  renderFailed() {
    this._set(`
      <div class="support-onne__state support-onne__card support-onne__card--narrow">
        <p class="support-onne__state-msg">${this.t.verify_failed}</p>
        ${this._talkTeamLink()}
      </div>
    `);
  }

  renderUnavailable() {
    this._set(`
      <div class="support-onne__state support-onne__card support-onne__card--narrow">
        <p class="support-onne__state-msg">${this.t.service_unavailable}</p>
        ${this._talkTeamLink()}
      </div>
    `);
  }

  renderNetworkError(orderNumber, factor) {
    this._set(`
      <div class="support-onne__state support-onne__card support-onne__card--narrow">
        <p class="support-onne__state-msg">${this.t.network_error}</p>
        <button class="support-onne__btn" type="button" data-retry>${this.t.retry}</button>
      </div>
    `);
    this.root.querySelector('[data-retry]').addEventListener('click', () => {
      if (orderNumber && factor) this._lookup(orderNumber, factor);
      else this.renderIdentify();
    });
  }

  // "I can't find my order number" → resolve it for the customer. Ask email +
  // phone(last4)/postal code, look it up in Shopify and reveal the number + status.
  // Security: the backend only reveals it when BOTH factors match the order.
  renderNoOrder() {
    const t = this.t;
    this._set(`
      <h1 class="support-onne__title">${t.find_title}</h1>
      <p class="support-onne__subtitle">${t.find_intro}</p>
      <form class="support-onne__card support-onne__card--narrow" data-find-form novalidate>
        <div class="support-onne__field" data-field-femail>
          <label class="support-onne__label" for="of-email">${t.find_email_label}</label>
          <input class="support-onne__input" id="of-email" name="femail" type="email" autocomplete="off" placeholder="${t.find_email_ph}" />
          <p class="support-onne__error" data-err-femail hidden></p>
        </div>
        <div class="support-onne__field" data-field-ffactor>
          <label class="support-onne__label" for="of-factor">${t.find_factor_label}</label>
          <input class="support-onne__input" id="of-factor" name="ffactor" type="text" autocomplete="off" placeholder="${t.find_factor_ph}" />
          <p class="support-onne__error" data-err-ffactor hidden></p>
        </div>
        <input type="text" name="company" tabindex="-1" autocomplete="off" aria-hidden="true" data-hp
               style="position:absolute;left:-9999px;width:1px;height:1px;opacity:0" />
        <button class="support-onne__btn" type="submit" data-submit>${t.find_cta}</button>
        <button class="support-onne__link support-onne__link--center" type="button" data-back>← ${t.cta}</button>
      </form>
    `);
    const form = this.root.querySelector('[data-find-form]');
    form.addEventListener('submit', (e) => { e.preventDefault(); this._onSubmitFind(form); });
    this.root.querySelector('[data-back]').addEventListener('click', () => this.renderIdentify());
  }

  _detectFindFactor(raw) {
    const v = (raw || '').trim();
    if (!v) return null;
    // Exactly 4 digits → phone last 4. Otherwise a postal code (ES = 5 digits; other = alnum).
    if (/^\d{4}$/.test(v)) return { phone_last4: v };
    return { postal_code: v.toUpperCase() };
  }

  _onSubmitFind(form) {
    const t = this.t;
    const emailEl = form.querySelector('#of-email');
    const factorEl = form.querySelector('#of-factor');
    form.querySelectorAll('.support-onne__field').forEach((f) => f.classList.remove('support-onne__field--error'));
    form.querySelectorAll('.support-onne__error').forEach((e) => { e.hidden = true; });
    const email = (emailEl.value || '').trim();
    const factorRaw = (factorEl.value || '').trim();
    let bad = false;
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { this._fieldError(form, 'femail', t.need_email); bad = true; }
    const factor = this._detectFindFactor(factorRaw);
    if (!factor) { this._fieldError(form, 'ffactor', t.need_find_factor); bad = true; }
    if (bad) { (email ? factorEl : emailEl).focus(); return; }
    const hpEl = form.querySelector('[data-hp]');
    this._findOrder(email, factor, hpEl ? hpEl.value : '');
  }

  async _findOrder(email, factor, hp) {
    this.renderLoading();
    if (!this.endpoint) { this.renderUnavailable(); return; }
    let resp;
    try {
      resp = await fetch(`${this.endpoint}/api/support/order/find`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, second_factor: factor, hp: hp || '', lang: this.lang }),
      });
    } catch (e) { this._findNetworkError(); return; }
    if (resp.status === 503) { this.renderUnavailable(); return; }
    let data;
    try { data = await resp.json(); } catch (e) { this._findNetworkError(); return; }
    if (data && data.found && data.verified && data.order_name) {
      this.renderFindResult(data);
      return;
    }
    // Generic not-found (never reveals whether the email exists) → graceful + team.
    this.renderFindNotFound();
  }

  // Reveal the found number, then its status (Diego: número + estado).
  renderFindResult(data) {
    const order = data.order_name;
    this.renderResult(data, order);
    const banner = document.createElement('div');
    banner.className = 'support-onne__state';
    banner.style.marginBottom = '1.25rem';
    const p = document.createElement('p');
    p.className = 'support-onne__state-msg';
    p.textContent = this.t.find_found.replace('{order}', order);
    banner.appendChild(p);
    this.root.insertBefore(banner, this.root.firstChild);
  }

  renderFindNotFound() {
    this._set(`
      <div class="support-onne__state support-onne__card support-onne__card--narrow">
        <p class="support-onne__state-msg js-msg"></p>
        ${this._talkTeamLink()}
        <button class="support-onne__link support-onne__link--center" type="button" data-back>← ${this.t.cta}</button>
      </div>
    `);
    this.root.querySelector('.js-msg').textContent = this.t.find_not_found;
    this.root.querySelector('[data-back]').addEventListener('click', () => this.renderIdentify());
  }

  _findNetworkError() {
    this._set(`
      <div class="support-onne__state support-onne__card support-onne__card--narrow">
        <p class="support-onne__state-msg">${this.t.network_error}</p>
        <button class="support-onne__btn" type="button" data-retry>${this.t.retry}</button>
      </div>
    `);
    this.root.querySelector('[data-retry]').addEventListener('click', () => this.renderNoOrder());
  }

  // -------------------------------------------------------------- demo (QA local)
  _renderState(state, orderNumber) {
    switch (state) {
      case 'preparing': return this.renderResult({ verified: true, status: 'processing' }, orderNumber);
      case 'in_transit': return this.renderResult({ verified: true, status: 'shipped', carrier_status: { carrier: 'SEUR' }, tracking_url: 'https://www.seur.com/livetracking/' }, orderNumber);
      case 'delivered': return this.renderResult({ verified: true, status: 'delivered' }, orderNumber);
      case 'incident': return this.renderIncident(orderNumber);
      case 'retry': return this.renderIdentify(this.t.verify_retry);
      case 'failed': return this.renderFailed();
      case 'unavailable': return this.renderUnavailable();
      case 'network': return this.renderNetworkError(orderNumber, { email: 'demo@onne.com' });
      case 'no_order': return this.renderNoOrder();
      default: return this.renderIdentify();
    }
  }
}

if (!customElements.get('order-status-onne')) {
  customElements.define('order-status-onne', OrderStatusOnne);
}
